// Single source of truth for the kitchen preparation timer: canonical start/deadline resolution,
// minute-only display rounding, urgency thresholds, progress and reminder deduplication.

export const PREP_ACTIVE_STATUSES = ["accepted", "preparing"];
export const REMINDER_MIN_DURATION_MS = 20 * 60 * 1000; // long orders only (>= 20 min)
export const REMINDER_THRESHOLDS = [10, 5];             // minutes remaining
export const EARLY_READY_THRESHOLD_MS = 10 * 60 * 1000; // early-READY confirmation threshold

/**
 * Exact canonical remaining ms until the pickup deadline (never derived from rounded UI text).
 * Returns null when no canonical deadline exists (legacy order) — the caller must then confirm.
 */
export function remainingMsUntilDeadline(o, serverNow) {
  const dl = orderDeadlineMs(o);
  return dl == null ? null : dl - serverNow;
}

/** Manual PREPARING → READY needs an explicit confirmation when it is far too early. */
export function needsEarlyReadyConfirm(o, serverNow) {
  const rem = remainingMsUntilDeadline(o, serverNow);
  return rem == null || rem >= EARLY_READY_THRESHOLD_MS;
}

/** Canonical pickup/preparation deadline (ms) — same field the board already sorts by. */
export function orderDeadlineMs(o) {
  if (o?.pickup_deadline) { const t = Date.parse(o.pickup_deadline); if (!isNaN(t)) return t; }
  const time = o?.confirmed_pickup_time || o?.pickup_time;
  if (o?.pickup_date && time && /^\d{2}:\d{2}$/.test(time)) {
    const t = Date.parse(`${o.pickup_date}T${time}:00`);
    if (!isNaN(t)) return t;
  }
  return null;
}

/**
 * Canonical preparation start (ms). PONESI online orders start when accepted (accepted_at).
 * VISTA / RESTAURANT_INTERNAL orders are created directly in PREPARING, so their created_at is
 * the canonical preparation start (accepted_at is persisted there too and is preferred).
 * Never fabricated from created_at for online orders — that would include NEW waiting time.
 */
export function orderPreparationStartMs(o) {
  if (o?.accepted_at) { const t = Date.parse(o.accepted_at); if (!isNaN(t)) return t; }
  if (o?.order_source === "RESTAURANT_INTERNAL" && o?.created_at) {
    const t = Date.parse(o.created_at); if (!isNaN(t)) return t;
  }
  return null;
}

/** Minute-only display: ceil for remaining, floor (min 1) for late. */
export function displayRemainingMinutes(remainingMs) {
  return Math.ceil(remainingMs / 60000);
}
export function displayLateMinutes(lateMs) {
  return Math.max(1, Math.floor(lateMs / 60000));
}

/** One urgency rule for the whole partner kitchen UI. */
export function urgencyTone(remainingMs) {
  if (remainingMs <= 0) return "late";
  const mins = displayRemainingMinutes(remainingMs);
  if (mins >= 5) return "green";
  if (mins > 2) return "orange";
  return "red";
}

export const TONE_BAR = {
  green: "bg-brand-green",
  orange: "bg-brand-orange",
  red: "bg-brand-red",
  late: "bg-brand-red",
};

/** Real elapsed-time progress 0..100 from canonical start → canonical deadline. */
export function prepProgressPct(startMs, deadlineMs, now) {
  if (startMs == null || deadlineMs == null) return null;
  const total = deadlineMs - startMs;
  if (total <= 0) return 100;
  return Math.min(100, Math.max(0, ((now - startMs) / total) * 100));
}

/** Confirmed preparation duration (ms) from the CURRENT canonical start/deadline pair. */
export function prepDurationMs(o) {
  const s = orderPreparationStartMs(o), d = orderDeadlineMs(o);
  if (s == null || d == null) return null;
  return d - s;
}

/** Manually scheduled Vista order: shown immediately in VISTA/LOKAL, but preparation is due later. */
export function isScheduledVista(o) {
  return o?.pickup_type === "internal_scheduled" && !!o?.prep_start_at;
}

/** Canonical preparation-start ms for a scheduled Vista order (server timestamp). */
export function scheduledPrepStartMs(o) {
  if (!o?.prep_start_at) return null;
  const t = Date.parse(o.prep_start_at);
  return isNaN(t) ? null : t;
}

/** Generic 10/5 reminders never apply to scheduled Vista orders — they get their own two. */
export function isReminderEligible(o) {
  if (isScheduledVista(o)) return false;
  return _isReminderEligible(o);
}

function _isReminderEligible(o) {
  const dur = prepDurationMs(o);
  return dur != null && dur >= REMINDER_MIN_DURATION_MS;
}

// ---- device-local, exactly-once reminder state (never part of canonical order state) ----
const LS_KEY = "ponesi.prepReminders.v1";
const RETENTION_MS = 6 * 60 * 60 * 1000;

function readStore() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}") || {}; } catch { return {}; }
}
function writeStore(obj) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(obj)); } catch {}
}

export function reminderKey(orderId, deadlineMs, threshold) {
  return `${orderId}:${deadlineMs}:${threshold}`;
}

export function hasReminderFired(key) {
  return key in readStore();
}

/** Marks a reminder as fired (survives refresh/remount) and prunes stale entries. */
export function markReminderFired(keys) {
  const store = readStore();
  const now = Date.now();
  for (const k of Object.keys(store)) {
    if (now - (store[k] || 0) > RETENTION_MS) delete store[k];
  }
  for (const k of keys) store[k] = now;
  writeStore(store);
}

let _channel = null;
export function reminderChannel() {
  if (_channel === null) {
    try { _channel = new BroadcastChannel("ponesi-prep-reminders"); } catch { _channel = false; }
  }
  return _channel || null;
}
